import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation26() {
  const { lesson, course, accent } = findLesson("piano-foundation", "26");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Five black-note major scales: <strong>D♭, E♭, G♭ (F♯), A♭, B♭</strong>. The pinky is rarely used — the three middle fingers do most of the work.</Highlight>
        <Highlight><strong>The crossing rule</strong>: ascending, cross the thumb whenever a <em>white note follows a black note</em>. Descending, cross the middle (or ring) finger whenever a <em>black note follows a white note</em>.</Highlight>
        <Highlight>Most scales start on the <strong>index finger</strong>. Some jazz players prefer thumb on the black key — both are fine; pick whichever feels comfortable.</Highlight>
        <Highlight>The black scales actually <strong>grip better</strong> than the white scales — your fingers naturally fall into the shape. White scales feel slipperier despite being visually simpler.</Highlight>
        <Highlight>Don't leave a <strong>gap at the crossing point</strong>. The crossing should be invisible — same tempo before and after the thumb tucks under.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The two crossing rules" title="One simple law that drives every black-scale fingering" accent={accent.hex}>
        <Pull>
          In the ascending, whenever a white note came after the black note, then you
          cross your thumb. In the descent, whenever a black note comes after a white
          note, you cross either the middle finger for the most part, sometimes the
          ring, sometimes the index finger.
        </Pull>

        <p>
          Most teaching tries to memorise different fingerings for each scale. The lesson replaces all of that with one rule that handles every black scale automatically:
          watch what colour comes next, and cross only when the colour <em>changes</em>.
        </p>

        <Vocab>
          <Term name="Crossing event" def="the moment the thumb tucks under (going up) or the middle finger crosses over (coming down)." />
          <Term name="A♭ major" def="A♭, B♭, C, D♭, E♭, F, G — four flats. Starts and ends on a black key." />
          <Term name="E♭ major" def="E♭, F, G, A♭, B♭, C, D — three flats." />
          <Term name="B♭ major" def="B♭, C, D, E♭, F, G, A — two flats." />
          <Term name="D♭ major" def="D♭, E♭, F, G♭, A♭, B♭, C — five flats. Three black keys in a row." />
          <Term name="G♭ (F♯) major" def="G♭ (= F♯) major. Six flats — or six sharps if spelled the other way." />
        </Vocab>
      </Insight>

      <Insight eyebrow="A♭ major" title="The first black scale to learn" accent={accent.hex}>
        <p>
          A♭ major has four flats: <strong>A♭, B♭, C, D♭, E♭, F, G</strong>. Start with the
          index finger on A♭. Walk through the rule:
        </p>

        <Steps>
          <Step><strong>A♭</strong> (index, 2). Next note B♭ is also black — no cross.</Step>
          <Step><strong>B♭</strong> (middle, 3). Next is C — white follows black. <em>Crossing event.</em></Step>
          <Step><strong>C</strong> (thumb, 1). Next is D♭ — black follows white in ascending direction, but no cross needed yet (only cross when colour goes black → white).</Step>
          <Step><strong>D♭</strong> (index, 2), <strong>E♭</strong> (middle, 3). Both black — no cross.</Step>
          <Step><strong>F</strong> — white follows black. <em>Cross again.</em></Step>
          <Step><strong>F</strong> (thumb), <strong>G</strong> (index), then back to <strong>A♭</strong> on the middle to close the octave.</Step>
        </Steps>

        <Tip>
          Coming down: A♭ - G - F - E♭ - D♭ - C - B♭ - A♭. The crossings happen on the
          black-after-white moments — F→E♭ and C→B♭. Cross the middle finger over the thumb
          at those points.
        </Tip>
      </Insight>

      <Insight eyebrow="The other four" title="E♭, B♭, D♭, G♭ — same rule" accent={accent.hex}>
        <ul className="space-y-2 text-[14px]">
          <li>
            <strong>E♭ major</strong> — start with the index. The crossings land on E♭→F
            (white follows black) and B♭→C (white follows black). Coming down, cross the
            <em> ring finger</em> here, not the middle — the preference for E♭ in
            particular: <em>"Cross the ring finger and I think this is easy to play. The
            other one's a bit juggly."</em>
          </li>
          <li>
            <strong>B♭ major</strong> — same shape as E♭. Start on the index. Crossings on
            B♭→C and E♭→F.
          </li>
          <li>
            <strong>D♭ major</strong> — three black notes in a row at the start (D♭-E♭-F♭...
            actually D♭-E♭-F but F is white). Easy because the black-key clusters do the
            walking. Start on the index.
          </li>
          <li>
            <strong>G♭ major</strong> (= F♯ major) — the most "all-black" of the scales.
            Start on the index, get into the natural finger order quickly, cross when white
            follows black.
          </li>
        </ul>

        <Pull>
          You'll end up realising that the black scales are a lot more easy on the grip
          than the white scales. Somehow white scales feel a bit more slippery than the
          black scales.
        </Pull>
      </Insight>

      <Insight eyebrow="Two valid starting fingers" title="Index or thumb · pick what feels comfortable" accent={accent.hex}>
        <p>
          Most pianists start each black scale on the <strong>index finger</strong> sitting
          on the first black key. But some jazz players prefer to start with the
          <strong> thumb on the black key</strong>. Both are fine — the crossing rule
          handles either approach.
        </p>

        <Tip>
          If you start with the thumb on a black key, slightly <strong>curve the thumb</strong>
          rather than keeping it straight. A straight thumb on a black key creates a small
          glitch in the wrist that can compound over a long passage.
        </Tip>
      </Insight>

      <Insight eyebrow="The single golden rule" title="Don't leave a gap at the cross" accent={accent.hex}>
        <p>
          Whatever else changes between scales, one thing must stay constant:{" "}
          <strong>the crossing must be invisible</strong>. The pulse continues, the tempo
          doesn't dip, no audible gap appears. If a listener can hear the moment your thumb
          tucks under, your thumb arrived too late.
        </p>

        <KeyIdea>
          The thumb prepares early. Same rule as the white scales (and the Pendulum): the
          thumb is hovering over the next key while the previous finger is still pressed.
          That early arrival is what makes the cross sound smooth.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Practice path" title="One scale a day · five days for the lot" accent={accent.hex}>
        <Steps>
          <Step><strong>Day 1</strong> · A♭ major. Both directions, slow. Sing the swaras.</Step>
          <Step><strong>Day 2</strong> · E♭ major. Pay attention to the ring-finger cross descending.</Step>
          <Step><strong>Day 3</strong> · B♭ major. Same shape as E♭ on a different key.</Step>
          <Step><strong>Day 4</strong> · D♭ major. The three-black-key opening makes it surprisingly easy.</Step>
          <Step><strong>Day 5</strong> · G♭ major. Mostly black notes — fly through it.</Step>
        </Steps>

        <Tip>
          Add the LH pulse from Lesson 25 — root, then root + octave toggle. Once the LH
          and RH lock together, build speed: crotchets → quavers → sixteenths.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-27"
        label="Lesson 27 · Minor & Exotic Scales Ascending & Descending"
        why="With all 12 major scales now in your hands, the next lesson opens up the minor scales — natural, harmonic, melodic — and a few exotic scales for variety."
      />
    </LessonShell>
  );
}
